import { config } from '../config.ts';
import { createStorageBuffer, createUniformBuffer } from '../gpu/buffers.ts';
import { createComputePipeline } from '../gpu/pipelines.ts';
import type { HeightmapModule } from './heightmap.ts';
import type { BiomeModule } from './biome.ts';
import type { RoadsModule } from './roads.ts';
import hashWgsl from './hash.wgsl?raw';
import buildingsWgsl from './buildings.wgsl?raw';

const wgsl = hashWgsl + '\n' + buildingsWgsl;

export interface BuildingsModule {
  /** Per-instance data: vec3 pos, vec2 footprint, f32 height, u32 styleId. */
  readonly instanceBuffer: GPUBuffer;
  /** Single-u32 atomic counter; first u32 = number of instances written. */
  readonly countBuffer: GPUBuffer;
  /** Cap. Same as `config.maxBuildingInstances`. Both buffers were sized to this. */
  readonly maxInstances: number;
  destroy(): void;
}

const INSTANCE_STRIDE = 32;

export async function createBuildings(
  device: GPUDevice,
  height: HeightmapModule,
  biome: BiomeModule,
  roads: RoadsModule,
  opts: { seed: number },
): Promise<BuildingsModule> {
  const max = config.maxBuildingInstances;

  // VERTEX usage so render-buildings can bind this directly as an instanced vertex buffer.
  const instanceBuffer = createStorageBuffer(device, max * INSTANCE_STRIDE, {
    label: 'buildings.instances',
    usageExtra: GPUBufferUsage.VERTEX,
  });
  // 16 bytes (>= 4) so a uniform-buffer min-size of 16 is honored too if rebound elsewhere.
  const countBuffer = createStorageBuffer(device, 16, { label: 'buildings.count' });
  device.queue.writeBuffer(countBuffer, 0, new Uint32Array([0, 0, 0, 0]));

  // Params: u32 seed, f32 worldSize, f32 worldHeight, u32 heightmapSize,
  //         u32 maxInstances, u32 cellStride, f32 roadRadius, f32 densityScale,
  //         f32 footprintRangeMax, f32 footprintMin, f32 footprintMax,
  //         f32 heightBase, f32 heightMax, f32 maxAspect, f32 villageDensityScale,
  //         + 4 trailing bytes of pad → 64 bytes total.
  const paramsBuffer = createUniformBuffer(device, 64, 'buildings.params');
  const paramsBytes = new ArrayBuffer(64);
  const paramsU32 = new Uint32Array(paramsBytes);
  const paramsF32 = new Float32Array(paramsBytes);
  paramsU32[0] = opts.seed >>> 0;
  paramsF32[1] = config.worldSize;
  paramsF32[2] = config.worldHeight;
  paramsU32[3] = config.heightmapSize >>> 0;
  paramsU32[4] = max >>> 0;
  paramsU32[5] = config.buildingCellStride >>> 0;
  paramsF32[6] = config.roadRadius;
  paramsF32[7] = config.buildingDensityScale;
  paramsF32[8] = config.buildingFootprintRangeMax;
  paramsF32[9] = config.buildingFootprintMin;
  paramsF32[10] = config.buildingFootprintMax;
  paramsF32[11] = config.buildingHeightBase;
  paramsF32[12] = config.buildingHeightMax;
  paramsF32[13] = config.buildingMaxAspect;
  paramsF32[14] = config.villageDensityScale;
  device.queue.writeBuffer(paramsBuffer, 0, paramsBytes);

  const pipeline = createComputePipeline(device, wgsl, 'main', 'auto', 'buildings');
  // Post-pass that clamps the atomic counter back to maxInstances — drawIndirect
  // consumes the counter as instanceCount, and overflowing appends only drop the
  // write, not the increment. Its auto layout reflects just count + params.
  const clampPipeline = createComputePipeline(device, wgsl, 'clamp_count', 'auto', 'buildings.clamp');

  const bindGroup = device.createBindGroup({
    label: 'buildings.bindGroup',
    layout: pipeline.getBindGroupLayout(0),
    entries: [
      { binding: 0, resource: height.view },
      { binding: 1, resource: biome.view },
      { binding: 2, resource: roads.maskView },
      { binding: 3, resource: { buffer: countBuffer } },
      { binding: 4, resource: { buffer: instanceBuffer } },
      { binding: 5, resource: { buffer: paramsBuffer } },
      { binding: 6, resource: roads.jfaView },
    ],
  });

  const clampBindGroup = device.createBindGroup({
    label: 'buildings.clamp.bindGroup',
    layout: clampPipeline.getBindGroupLayout(0),
    entries: [
      { binding: 3, resource: { buffer: countBuffer } },
      { binding: 5, resource: { buffer: paramsBuffer } },
    ],
  });

  // Dispatch: ceil(heightmapSize / cellStride / 8) per dim.
  const candidates = Math.ceil(config.heightmapSize / config.buildingCellStride);
  const groups = Math.ceil(candidates / 8);

  const encoder = device.createCommandEncoder({ label: 'buildings.encoder' });
  const pass = encoder.beginComputePass({ label: 'buildings.pass' });
  pass.setPipeline(pipeline);
  pass.setBindGroup(0, bindGroup);
  pass.dispatchWorkgroups(groups, groups, 1);
  pass.end();
  const clampPass = encoder.beginComputePass({ label: 'buildings.clamp.pass' });
  clampPass.setPipeline(clampPipeline);
  clampPass.setBindGroup(0, clampBindGroup);
  clampPass.dispatchWorkgroups(1, 1, 1);
  clampPass.end();
  device.queue.submit([encoder.finish()]);

  paramsBuffer.destroy();

  return {
    instanceBuffer,
    countBuffer,
    maxInstances: max,
    destroy() {
      instanceBuffer.destroy();
      countBuffer.destroy();
    },
  };
}
